import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchCustomers,
  fetchCustomer,
  createCustomer,
  updateCustomer,
} from "../api/customer";
import produce from "immer";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  customer: {},
  data: [],
  count: 0,
  currentCount: 0,
};

export const customersController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const updateCustomerAsync = createAsyncThunk(
  "customers/updateCustomerAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateCustomer(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_customer");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_customer");
    }
  }
);

export const createCustomerAsync = createAsyncThunk(
  "customers/createCustomerAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createCustomer(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_customer");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_customer");
    }
  }
);

export const getCustomers = createAsyncThunk(
  "customers/getCustomers",
  async (controller = customersController, { rejectWithValue }) => {
    try {
      const response = await fetchCustomers(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_customers");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_customers");
    }
  }
);

export const getCustomerAsync = createAsyncThunk(
  "customers/getCustomerAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchCustomer(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_customer");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_customer");
    }
  }
);

const customerSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCustomers.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        // Infinite scroll: append page results instead of replacing
        if (action.meta?.arg?.append) {
          const existing = new Set((state.data || []).map((d) => d.id));
          const incoming = (action.payload.data || []).filter(
            (d) => !existing.has(d.id)
          );
          state.data = [...(state.data || []), ...incoming];
        } else {
          state.data = action.payload.data;
        }
        state.count = action.payload.count;
        state.currentCount = action.payload.currentCount;
      })
      .addCase(getCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente kundene.");
      })
      .addCase(getCustomerAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getCustomerAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.customer = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getCustomerAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente kunden.");
      })
      .addCase(updateCustomerAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateCustomerAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.customer = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateCustomerAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere kunden.");
      })
      .addCase(createCustomerAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createCustomerAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          if (action.payload?.id || action.payload?.data?.id) {
            draftState.data.push(action.payload?.data || action.payload);
          }
          draftState.isLoading = false;
        });
      })
      .addCase(createCustomerAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette kunden.");
      });
  },
});

export const { reducer } = customerSlice;

export default customerSlice;

export const selectCustomerById = (state, customerId) =>
  state.customers.customers.find((customer) => customer.id == customerId);
